import React, { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import { platforms } from '../mock/mock';
import { products, pricePer1000, getUnitLabel, getEffectiveMinQty } from '../lib/catalog';
import { ShoppingCart, Check, Filter, TrendingUp, Zap, Star, ArrowRight } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { PlatformLogo } from '../components/PlatformLogo';

const Collection = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const platform = platforms.find(p => p.slug === slug) || null;
  const items = slug ? (products[slug] || []).map(x => ({ ...x, _platform: slug })) : Object.entries(products).flatMap(([k, arr]) => arr.map(x => ({ ...x, _platform: k })));

  const [sortBy, setSortBy] = useState('recommend');

  const sortedItems = useMemo(() => {
    const arr = [...items];
    const p1k = (p) => pricePer1000({ ...p, platformKey: p._platform });
    if (sortBy === 'price-asc') arr.sort((a, b) => p1k(a) - p1k(b));
    if (sortBy === 'price-desc') arr.sort((a, b) => p1k(b) - p1k(a));
    return arr;
  }, [items, sortBy]);

  return (
    <div>
      <AnnouncementBar />
      <Header />

      <div className="relative border-b border-white/5">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
          <div className="flex items-center gap-2 text-white/50 text-xs mb-6">
            <Link to="/" className="hover:text-white transition">Accueil</Link>
            <span>/</span>
            <Link to="/collections" className="hover:text-white transition">Boutique</Link>
            {platform && <><span>/</span><span className="text-white">{platform.name}</span></>}
          </div>
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="text-[#ff8247] text-xs font-bold uppercase tracking-widest mb-3">
                {platform ? 'Réseau' : 'Tout notre catalogue'}
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold text-white leading-none">
                {platform ? (<>Booster <span className="gradient-text">{platform.name}</span></>) : (<>Boutique <span className="gradient-text">SociBoost</span></>)}
              </h1>
              {platform && <p className="text-white/60 mt-4 max-w-2xl">{platform.desc}</p>}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-white/60 text-xs">
                <Filter className="w-3.5 h-3.5" /> Trier par
              </div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                className="bg-white/5 border border-white/10 text-white text-sm rounded-full px-4 py-2 focus:outline-none focus:border-[#ff8247]">
                <option value="recommend">Recommandé</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {!platform && (
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-10">
          <div className="flex flex-wrap gap-2 mb-2">
            {platforms.map(p => (
              <button key={p.id} onClick={() => navigate(`/collections/${p.slug}`)}
                className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 hover:border-[#ff8247]/50 transition">
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedItems.map((product) => {
            const currentPlatform = platform || platforms.find(p => p.slug === product._platform);
            const price1k = pricePer1000({ ...product, platformKey: product._platform });
            const unitLabel = getUnitLabel(product.name);
            return (
              <div key={product.id} className="glass-card rounded-3xl p-6 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${currentPlatform?.color}18` }}>
                      <PlatformLogo platform={currentPlatform?.id} className="w-5 h-5" color={currentPlatform?.id === 'instagram' ? undefined : currentPlatform?.color} />
                    </div>
                    <span className="text-white/60 text-xs font-semibold">{currentPlatform?.name}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[#00b67a] text-xs font-semibold">
                    <Star className="w-3 h-3" fill="currentColor" /> 4.8
                  </div>
                </div>

                <button onClick={() => navigate(`/product/${product.id}`)} className="text-left">
                  <h3 className="text-white text-xl font-bold mb-1 hover:text-[#ff8247] transition" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{product.name}</h3>
                </button>
                <div className="text-white/50 text-sm mb-4">Livraison progressive · Sans mot de passe</div>

                <div className="grid grid-cols-2 gap-3 mb-5 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest font-semibold">Min</div>
                    <div className="text-white text-sm font-bold">{Math.min(Number(product.max || 0), getEffectiveMinQty({ ...product, platformKey: product._platform })).toLocaleString('fr-FR')}</div>
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest font-semibold">Max</div>
                    <div className="text-white text-sm font-bold">{Number(product.max || 0).toLocaleString('fr-FR')}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-white/50 text-xs mb-5">
                  <div className="flex items-center gap-1"><Zap className="w-3.5 h-3.5 text-[#ff8247]" /> Livré en 12h</div>
                  <div className="w-1 h-1 rounded-full bg-white/20" />
                  {product.refill && <div className="flex items-center gap-1 text-[#00b67a]"><Check className="w-3.5 h-3.5" /> Refill</div>}
                  {!product.refill && <div className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-[#ff8247]" /> Premium</div>}
                </div>

                <div className="flex items-center justify-between mt-auto pt-5 border-t border-white/5 gap-3">
                  <div className="min-w-0">
                    <div className="text-white/40 text-xs">À partir de</div>
                    <div data-testid={`collection-price-${product.id}`} className="text-white text-2xl font-bold leading-tight truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{price1k.toFixed(2)} <span className="text-base text-white/60">€</span></div>
                    <div className="text-white/50 text-[11px] font-medium mt-0.5 truncate">/ 1 000 {unitLabel}</div>
                  </div>
                  <button onClick={() => navigate(`/product/${product.id}`)} data-testid={`collection-choose-${product.id}`} className="btn-primary inline-flex items-center gap-2 text-sm flex-shrink-0">
                    Choisir <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Collection;
